export interface MetaTags {
  title: string
  description: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  canonical?: string
  jsonLd?: object | object[]
}

// Import centralized route meta configuration
import routeMetaConfig from '@/config/routeMeta.json'

// Convert JSON config to typed MetaTags
export const routeMetaTags: Record<string, MetaTags> = routeMetaConfig as Record<string, MetaTags>

// Function to update meta tags in the document head
export function updateMetaTags(meta: MetaTags): void {
  if (typeof document === 'undefined') return

  // Update title
  document.title = meta.title

  // Helper function to update or create meta tags
  const updateMetaTag = (name: string, content: string, property = false): void => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
    let metaTag = document.querySelector(selector) as HTMLMetaElement
    
    if (!metaTag) {
      metaTag = document.createElement('meta')
      if (property) {
        metaTag.setAttribute('property', name)
      } else {
        metaTag.setAttribute('name', name)
      }
      document.head.appendChild(metaTag)
    }
    
    metaTag.setAttribute('content', content)
  }

  // Helper function to update or create link tags
  const updateLinkTag = (rel: string, href: string): void => {
    const selector = `link[rel="${rel}"]`
    let linkTag = document.querySelector(selector) as HTMLLinkElement
    
    if (!linkTag) {
      linkTag = document.createElement('link')
      linkTag.setAttribute('rel', rel)
      document.head.appendChild(linkTag)
    }
    
    linkTag.setAttribute('href', href)
  }

  // Update basic meta tags
  updateMetaTag('title', meta.title)
  updateMetaTag('description', meta.description)
  
  if (meta.keywords) {
    updateMetaTag('keywords', meta.keywords)
  }

  // Update Open Graph tags
  updateMetaTag('og:title', meta.ogTitle || meta.title, true)
  updateMetaTag('og:description', meta.ogDescription || meta.description, true)
  updateMetaTag('og:type', 'website', true)
  
  if (meta.ogImage) {
    updateMetaTag('og:image', meta.ogImage, true)
  }
  
  if (meta.ogUrl) {
    updateMetaTag('og:url', meta.ogUrl, true)
  }

  // Update Twitter tags
  updateMetaTag('twitter:card', 'summary_large_image', true)
  updateMetaTag('twitter:title', meta.twitterTitle || meta.title, true)
  updateMetaTag('twitter:description', meta.twitterDescription || meta.description, true)
  
  if (meta.twitterImage) {
    updateMetaTag('twitter:image', meta.twitterImage, true)
  }

  // Update Google / Schema.org itemprop tags
  const updateItemprop = (name: string, content: string): void => {
    const selector = `meta[itemprop="${name}"]`
    let tag = document.querySelector(selector) as HTMLMetaElement
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('itemprop', name)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }
  updateItemprop('name', meta.ogTitle || meta.title)
  updateItemprop('description', meta.ogDescription || meta.description)
  if (meta.ogImage) {
    updateItemprop('image', meta.ogImage)
  }

  // Update canonical URL
  if (meta.canonical) {
    updateLinkTag('canonical', meta.canonical)
  }

  // Update JSON-LD structured data
  document.querySelectorAll('script[data-vue-jsonld]').forEach(el => el.remove())
  if (meta.jsonLd) {
    const items = Array.isArray(meta.jsonLd) ? meta.jsonLd : [meta.jsonLd]
    items.forEach(item => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-vue-jsonld', '')
      script.textContent = JSON.stringify(item)
      document.head.appendChild(script)
    })
  }
}

// Function to get meta tags for a specific route
export function getMetaTagsForRoute(route: string): MetaTags | null {
  return routeMetaTags[route] || null
}
